import { db } from './config'
import { collection, doc, getDoc, getDocs, query, orderBy, where, limit } from 'firebase/firestore'
import { getAuthHeaders } from './authHeaders'
import { getDocsWhereIn } from './queries'

const CLOUD_FUNCTIONS_BASE_URL = process.env.REACT_APP_PAYMENT_API || 'http://localhost:5001/card-market-cloud-functions/us-central1/api'

// Mirrors the backend's applyPriceMultiplier. Both sides must round
// identically and read the multiplier from the same place (the product
// document), or a buyer is shown one price and charged another.
const MIN_PRICE_MULTIPLIER = 0.01
const MAX_PRICE_MULTIPLIER = 100

export const readPriceMultiplier = (value) => {
    const multiplier = Number(value)
    if (!Number.isFinite(multiplier) || multiplier < MIN_PRICE_MULTIPLIER || multiplier > MAX_PRICE_MULTIPLIER) {
        return 1
    }
    return multiplier
}

export const applyPriceMultiplier = (price, multiplier) =>
    Math.round((Number(price) || 0) * readPriceMultiplier(multiplier) * 100) / 100

// Whether a shopper can buy this item at all: the owner's own switch, or a
// marketplace-wide block on the store it belongs to.
export const isUnavailable = (card = {}) => card?.hidden === true || card?.store_blocked === true

const stripHtml = (value = '') => (String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim())

export const normalizeShopifyProduct = (product = {}) => {
    const variants = Array.isArray(product?.variants) ? product.variants : []
    const firstVariant = variants[0] || {}
    const basePrice = Number(firstVariant.price || 0)
    // The store's markup, stored on the product precisely so that this and
    // the checkout price come from one field.
    const priceMultiplier = readPriceMultiplier(product.marketplace_price_multiplier)
    const price = applyPriceMultiplier(basePrice, priceMultiplier)
    // `|| 1` only covers products with no variant data at all -- if variants
    // exist and sum to 0, that 0 must be preserved so out-of-stock items are
    // correctly filtered instead of being treated as having 1 unit in stock.
    const quantity = variants.length > 0
        ? variants.reduce((sum, variant) => sum + Number(variant.inventory_quantity || 0), 0)
        : 1
    // The store's role id, not a user id -- see getStoreSellerIds.
    const sellerId = product.seller_id || product.seller || ''
    const shopDomain = product.shop_domain || product.shopify_store_domain || ''
    const sellerDisplayName = product.seller_display_name || shopDomain || 'Shopify Store'

    return {
        // The Firestore document id, which is what every lookup, cart entry,
        // sale cardId and /shopify/:productId route key off. NOT
        // shopify_product_id: since stores were scoped, the document id is
        // `<shop>__<shopifyProductId>` and the two are different values.
        id: String(product.doc_id || product.id || ''),
        title: product.title || 'Shopify Product',
        description: stripHtml(product.body_html || product.description || 'Shopify managed product') || 'Shopify managed product',
        price,
        quantity,
        pictures: [product.image?.src || product.image || '/assets/images/image_item_default.png'],
        rarity: 'Shopify',
        category: product.marketplace_category || product.product_type || 'shopify',
        sub_category: product.marketplace_sub_category || product.product_type || 'shopify',
        // Whether the seller actually picked a sub-category. The fallback
        // above puts the Shopify product type in sub_category when they
        // didn't, and that is never one of the marketplace category's own
        // sub-categories -- so the browse filter has to be able to tell the
        // difference or it hides the listing from the very category it was
        // assigned to. See utils/listingFilters.
        sub_category_assigned: Boolean(String(product.marketplace_sub_category || '').trim()),
        // 'manual' (picked on the listing), 'mapping' (from the seller's
        // category mapping) or null (uncategorized).
        category_source: product.marketplace_category
            ? (product.marketplace_category_source || 'manual')
            : null,
        // The store owner's enable/disable switch. Hidden listings are
        // dropped from every browse list and can't be bought, but are still
        // returned by id so purchase history, sales and open offers render.
        hidden: product.marketplace_hidden === true,
        // An admin block on the whole store. Separate from `hidden` so the
        // owner's own switch still reads truthfully in their listings table
        // while the store is blocked.
        store_blocked: product.marketplace_store_blocked === true,
        // The Shopify price before the store's markup, and the markup
        // itself, for the owner's and admin's views.
        base_price: basePrice,
        price_multiplier: priceMultiplier,
        product_type: product.product_type || '',
        // Shopify's standard category: { id, name, full_name } or null.
        shopify_category: product.shopify_category || null,
        condition: 'new',
        released_year: new Date().getFullYear(),
        source: 'shopify',
        managed_by_shopify: true,
        inventory_source: 'shopify',
        order_destination: 'shopify',
        merchant_id: product.merchant_id || sellerId || 'shopify',
        seller_id: sellerId,
        seller: sellerId,
        seller_display_name: sellerDisplayName,
        shopify_product_id: product.shopify_product_id || product.id,
        shopify_store_domain: shopDomain,
        shop_domain: shopDomain
    }
}

export const mergeMarketplaceItems = (listings = [], shopifyProducts = []) => {
    const normalizedListings = Array.isArray(listings) ? listings : []
    const normalizedShopify = Array.isArray(shopifyProducts) ? shopifyProducts : []

    return [...normalizedListings, ...normalizedShopify].sort((a, b) => {
        const priceA = Number(a?.price || 0)
        const priceB = Number(b?.price || 0)
        return priceA - priceB
    })
}

/**
 * Returns { product, status } rather than a bare product.
 *
 * Collapsing "no such document" and "the read threw" into a single null left
 * the detail page rendering an empty shell with nothing to explain it --
 * indistinguishable from a slow load, and impossible to debug from the UI.
 * The two cases have completely different causes (a stale or wrong id vs.
 * security rules / connectivity), so they are reported separately.
 */
export const fetchShopifyProductByID = async (id) => {
    if (!id) {
        return { product: null, status: 'not_found' }
    }

    try {
        const snapshot = await getDoc(doc(db, 'shopify_products', id))

        if (!snapshot.exists()) {
            console.warn(`No shopify_products document with id "${id}"`)
            return { product: null, status: 'not_found' }
        }

        // Unlike getShopifyProducts() (grid/browse), the detail page mirrors
        // getListingByID's behavior for local listings: always return the
        // item if it exists, even out of stock, so it still opens with
        // "Add to Cart" disabled rather than the page dead-ending.
        return {
            product: normalizeShopifyProduct({ ...snapshot.data(), doc_id: snapshot.id }),
            status: 'ok'
        }
    } catch (error) {
        console.error('Error fetching Shopify product:', error)
        return { product: null, status: 'error', error }
    }
}

// Kept for callers that only care whether an item exists (Cart.js resolving a
// sale row's cardId, for one).
export const getShopifyProductByID = async (id) => (await fetchShopifyProductByID(id)).product

export const getShopifyProducts = async () => {
    try {
        const shopifyProductsRef = collection(db, 'shopify_products')
        const q = query(shopifyProductsRef, orderBy('title'))
        const snapshot = await getDocs(q)
        const products = []

        snapshot.forEach((doc) => {
            const product = normalizeShopifyProduct({ ...doc.data(), doc_id: doc.id })
            // Filtered here rather than in the query: listings synced before
            // the switch existed carry no marketplace_hidden field at all,
            // and a `where('marketplace_hidden', '==', false)` would drop
            // every one of them.
            if (product.quantity > 0 && !isUnavailable(product)) {
                products.push(product)
            }
        })

        return products
    } catch (error) {
        console.error('Error fetching Shopify products:', error)
        return []
    }
}

// A Shopify item is sold by its STORE, not by a marketplace user, so a sale's
// `receiver` (and a product's `seller_id`) is a store role id like
// "shopify:my-store.myshopify.com". Seller-side views therefore resolve an
// account to the stores it owns and query by those ids.
//
// shopify_settings holds each store's access token and is never readable from
// the client, so the mapping comes from the backend. Cached per uid for the
// page's lifetime: it only changes when an admin reassigns a store.
const storeCache = new Map()

// The stores an account owns, each with its shop domain, role id and price
// multiplier. Cached per uid for the page's lifetime, as the ids were
// before; pass { refresh: true } after changing a multiplier.
export const getMyStores = async (uid, { refresh = false } = {}) => {
    if (!uid) return []
    if (!refresh && storeCache.has(uid)) return storeCache.get(uid)

    try {
        const response = await fetch(
            `${CLOUD_FUNCTIONS_BASE_URL}/shopify/my-stores?uid=${encodeURIComponent(uid)}`,
            { headers: { ...(await getAuthHeaders()) } }
        )
        const payload = await response.json()
        const stores = payload?.stores || []
        storeCache.set(uid, stores)
        return stores
    } catch (error) {
        console.error('Error resolving stores:', error)
        return []
    }
}

export const getStoreSellerIds = async (uid) =>
    (await getMyStores(uid)).map((store) => store.seller_id).filter(Boolean)

// The account behind a store: who a notification or chat should reach, and
// whose name a shopper should see as the seller.
//
// Resolved here rather than stored on the product. A product's seller is the
// store, and the owner can change -- baking the owner's name into every
// product document would mean rewriting the whole catalogue on every
// handover, which is exactly what the store role id avoids.
//
// Cached for the page's lifetime; it only changes when an admin reassigns.
const storeOwnerCache = new Map()

export const getStoreOwner = async (shopDomain) => {
    const empty = { uid: '', displayName: '' }
    if (!shopDomain) return empty
    if (storeOwnerCache.has(shopDomain)) return storeOwnerCache.get(shopDomain)

    try {
        const response = await fetch(
            `${CLOUD_FUNCTIONS_BASE_URL}/shopify/store-owner?shop=${encodeURIComponent(shopDomain)}`,
            { headers: { ...(await getAuthHeaders()) } }
        )
        const payload = await response.json()
        const owner = {
            uid: payload?.owner_uid || '',
            displayName: payload?.owner_display_name || ''
        }
        storeOwnerCache.set(shopDomain, owner)
        return owner
    } catch (error) {
        console.error('Error resolving store owner:', error)
        return empty
    }
}

export const getStoreOwnerUid = async (shopDomain) => (await getStoreOwner(shopDomain)).uid

// The store this item belongs to, however the caller happens to hold it.
export const shopDomainOf = (card = {}) => card?.shop_domain || card?.shopify_store_domain || ''

// Who a shopper should see as the seller. Falls back to the shop domain when
// no owner is assigned yet, which is better than a blank name.
export const resolveSellerIdentity = async (card = {}) => {
    const shopDomain = shopDomainOf(card)
    if (card?.source !== 'shopify' && !card?.managed_by_shopify) {
        return { uid: card?.seller || card?.seller_id || '', displayName: '' }
    }

    const owner = await getStoreOwner(shopDomain)
    return { uid: owner.uid, displayName: owner.displayName || shopDomain }
}

// Who a message about this item should actually reach. For a Shopify item
// that is the store's owner; for a local listing it is the seller themself.
export const resolveContactUid = async (card = {}) => {
    if (card?.source === 'shopify' || card?.managed_by_shopify) {
        return await getStoreOwnerUid(shopDomainOf(card))
    }
    return card?.seller || card?.seller_id || ''
}

// Every id a user sells under: their own uid for local listings, plus one
// role id per store they own. Queried through getDocsWhereIn, which chunks
// past Firestore's 30-value `in` cap, so there is no ceiling on store count.
export const getSellerIdsForUser = async (uid) => {
    if (!uid) return []
    return [uid, ...(await getStoreSellerIds(uid))]
}

// Whether this account owns any connected Shopify store. Now a single
// backend lookup instead of scanning the product catalogue for a matching
// seller_id.
export const isShopifySeller = async (uid) => {
    if (!uid) return false
    return (await getStoreSellerIds(uid)).length > 0
}

// Unlike getShopifyProducts() (shopper browse grid), this is the store
// owner's own listing management view, so out-of-stock items are included.
export const getShopifyProductsBySellerId = async (uid) => {
    try {
        const sellerIds = await getStoreSellerIds(uid)
        if (sellerIds.length === 0) return []

        const docs = await getDocsWhereIn(collection(db, 'shopify_products'), 'seller_id', sellerIds)
        return docs.map((doc) => normalizeShopifyProduct({ ...doc.data(), doc_id: doc.id }))
    } catch (error) {
        console.error('Error fetching store Shopify products:', error)
        return []
    }
}

const callShopifyApi = async (path, method, body, fallbackError) => {
    const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}${path}`, {
        method,
        headers: {
            'Content-Type': 'application/json',
            ...(await getAuthHeaders())
        },
        body: body === undefined ? undefined : JSON.stringify(body)
    })
    const payload = await response.json()

    if (!response.ok || payload?.success === false) {
        throw new Error(payload?.error || fallbackError)
    }

    return payload
}

// The store role ids belonging to sellers the signed-in shopper has blocked.
//
// A Shopify listing's seller is the store it came from, not the account that
// owns the store, and that ownership lives in shopify_settings -- which the
// client can never read. So blocking an account can only be turned into a
// set of listings to hide on the backend.
//
// Throws on failure rather than returning []: ./blocks decides what a
// partial block list means, and silently returning none would show a buyer
// exactly the stores they asked never to see again.
export const getBlockedStoreSellerIds = async () => {
    const payload = await callShopifyApi('/shopify/blocked-store-sellers', 'GET', undefined, 'Failed to resolve blocked stores')
    return payload?.seller_ids || []
}

export const updateShopifyProductCategory = (productId, category, subCategory) =>
    callShopifyApi(`/shopify/products/${productId}/category`, 'POST', { category, subCategory }, 'Failed to update category')

// The store's markup for selling on CSE: every listing's price, and what a
// buyer is charged, is its Shopify price times this. 1 sells at the Shopify
// price, which is where every store starts.
export const setStorePriceMultiplier = (shop, multiplier) =>
    callShopifyApi('/shopify/stores/price-multiplier', 'POST', { shop, multiplier }, 'Failed to update price multiplier')

// Takes one listing off the marketplace, or puts it back. Nothing changes on
// Shopify itself -- the product still exists and still syncs.
export const setShopifyProductVisibility = (productId, hidden) =>
    callShopifyApi(`/shopify/products/${productId}/visibility`, 'POST', { hidden }, 'Failed to update listing visibility')

// The same category applied to a selection of listings in one request, so
// categorising a catalogue is not one round trip per product. The backend
// checks ownership of every id, and rejects the whole call if any one of
// them is not the caller's.
export const bulkUpdateShopifyProductCategory = (productIds, category, subCategory) =>
    callShopifyApi(
        '/shopify/products/bulk-category',
        'POST',
        { product_ids: productIds, category, subCategory },
        'Failed to update categories'
    )

// Drops a hand-picked category so the product follows the category mapping.
export const resetShopifyProductCategory = (productId) =>
    callShopifyApi(`/shopify/products/${productId}/category`, 'DELETE', undefined, 'Failed to reset category')

// { mappings, shopify_categories, listings_without_category } for the
// signed-in account. shopify_categories lists every Shopify category their
// listings use, with a count, read live from Shopify.
export const getShopifyCategoryMappings = () =>
    callShopifyApi('/shopify/category-mappings', 'GET', undefined, 'Failed to load category mapping')

// Replaces the whole mapping and applies it. overwrite: true also
// re-categorizes listings that already have a category; false only fills in
// uncategorized ones. Resolves to { mappings, updated, kept_existing }.
export const saveShopifyCategoryMappings = (mappings, overwrite) =>
    callShopifyApi('/shopify/category-mappings', 'PUT', { mappings, overwrite }, 'Failed to save category mapping')

// Applies the saved mapping, as above. Resolves to { updated, kept_existing }.
export const applyShopifyCategoryMappings = (overwrite) =>
    callShopifyApi('/shopify/category-mappings/apply', 'POST', { overwrite }, 'Failed to apply category mapping')
